<template>
  <card>
  <div class="container">
    <div class="row">
      <dropdown-with-valid-data
        :id="'severity'" 
        :labelName="'system_event.filter.severity'" 
        :defaultOptionName="'system_event.filter.all_severities'"
        :dropdownOptions="severities"
        @changeSelectedOption="onSeverityChanged"
        >
      </dropdown-with-valid-data>

      <dropdown-with-valid-data
        :id="'sourceType'" 
        :labelName="'system_event.filter.source_type'" 
        :defaultOptionName="'system_event.filter.all_source_types'"
        :dropdownOptions="sourceTypes"
        @changeSelectedOption="onSourceTypeChanged"
        >
      </dropdown-with-valid-data>

      <dropdown-with-valid-data
        :id="'acknowledgement'" 
        :labelName="'system_event.filter.acknowledgement'" 
        :defaultOptionName="'system_event.filter.all_acknowledgement'"
        :dropdownOptions="acknowledgments"
        @changeSelectedOption="onAcknowledgmentChanged"
        >
      </dropdown-with-valid-data>
    </div>

    <div class="row mt-3">
      <div class="col-md-6">
        <label>{{ this.$t('system_event.filter.source') }}</label>
          <input
            type="search"
            class="form-control"
            v-model="searchedSource"
            @input="onSourceSearchChanged"
            :placeholder="$t('system_event.filter.source')"
          />
      </div>
      <dropdown-with-valid-data class="col-md-6"
        :id="'eventType'" 
        :labelName="'system_event.filter.event_type'" 
        :defaultOptionName="'system_event.filter.all_event_types'"
        :dropdownOptions="eventTypes"
        @changeSelectedOption="onEventTypeChanged">
      </dropdown-with-valid-data>
    </div>
    <div class="row mt-3">
      <b-col>
        <b-form-group>
          <label>{{ $t('system_event.filter.date') }}</label>
          <b-form-datepicker 
            :placeholder="this.$t('datepicker.labelNoDateSelected')"
            v-model="selectedDate" 
            :locale="this.$t('datepicker.locale')"
            @input="filterTableDataByDate"
            class="datepicker"
            placement="bottom"
            :flip="false"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
          </b-form-datepicker>
        </b-form-group>
      </b-col>

      <b-col></b-col>
    </div>
    <p></p>

    <div>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>{{ this.$t('system_event.time') }}</th>
            <th>{{ this.$t('system_event.severity') }}</th>
            <th>{{ this.$t('system_event.source') }}</th>
            <th>{{ this.$t('system_event.sourceId') }}</th> 
            <th>{{ this.$t('system_event.data') }}</th>
            <th>{{ this.$t('system_event.acknowledge') }}</th>
            <th>{{ this.$t('system_event.actions') }}</th> 
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row) in paginatedData" :key="row.id">
            <td>{{ formatDate(row.creationTime) }}</td>
            <td>{{ getTranslationForTableRow("severity", getSeverityForEventType(row.eventType, severityLevels)) }}</td>
            <td>{{ getTranslationForTableRow("sourceType", row.source) }}</td>
            <td>{{ row.sourceId }}</td>
            <td>
              <EventDataRenderer
                :id="row.id" 
                :eventType="row.eventType" 
                :eventData="row.eventData"/>
            </td>
            <td class="acknowledgeColumn" ref="acknowledgeColumn">
              <acknowledge-button 
                :source="row.source" 
                :id="row.id" 
                :ack="row.acknowledgementType"
                @updateAck="onUpdateAck"/>
            </td>
            <td>
              <action-buttons 
                :source="row.source" 
                :id="row.id" 
                :sourceId="row.sourceId"
                :showVehicleOnMap="true"
                :showSendMessageToVehicle="showSendMessageToVehicle()"
                :showDisplayOnMap="true"
                :showDisplayInDisplayView="true"
                >
              </action-buttons>
            </td>
          </tr>
        </tbody>
      </table>
      <TablePagination
        :total-rows="totalRows"
        :per-page="itemsPerPage"
        @pageChange="onPageChange"
        :currentPage="page"
        @perPageChange="onPerPageChange"
        :extendedPagination="true"
        :perPageArray="[10, 25, 50]"/>
    </div>
  </div>
</card>
</template>

<script>
import AcknowledgeButton from '@/components/UIComponents/AcknowledgeButton.vue'
import ActionButtons from '@/components/UIComponents/ActionButtons.vue'
import DropdownWithValidData from '@/components/UIComponents/DropdownWithValidData.vue'
import EventDataRenderer from '@/components/UIComponents/EventDataRenderers/EventDataRenderer.vue'
import systemEvents from '@/store/dispatching/systemevents.js'
import moment from 'moment';

export default {
  name: 'SystemEventTable',
  components: { AcknowledgeButton, ActionButtons, DropdownWithValidData, EventDataRenderer },
  data() {
    return {
      dateFormat: 'YYYY-MM-DD[T]HH:mm:ssZZ',
      selectedSeverity: '',
      selectedSourceType: '',
      selectedEventType: '',
      searchedSource: '',
      selectedAcknowledgement: '',
      selectedDate: new moment().format('YYYY-MM-DD'),
      currentPage: 1,
      itemsPerPage: 10,
      dataUpdater: null,
			dataUpdateTime: null,
      listOfCurrentlyShownData: []
    }
  },
  computed: {
    items() {
        let systemEventsList = this.$store.state.systemEvents.systemEventsList;
        return systemEventsList ? systemEventsList : [];
    },
    severityLevels() {
        let severityLevelsList = this.$store.state.systemEvents.severityLevelsList;
        return severityLevelsList ? severityLevelsList : [];
    },
    totalPages() {
      return Math.ceil(this.filteredTableData.length / this.itemsPerPage)
    },
    totalRows() {
      return this.filteredTableData.length;
    },
    page() {
      return this.currentPage;
    },
    filteredTableData() {
      let data = this.items.filter(row => {
        const severity = this.getSeverityForEventType(row.eventType, this.severityLevels);
        return this.isLikeSearchedObject(this.selectedSourceType, row.source) &&
        this.isLikeSearchedEventType(this.selectedEventType, row.eventType, row.eventData) &&
        this.isLikeSearchedObject(this.selectedSeverity, severity) &&
        this.isLikeSearchedObject(this.searchedSource, row.sourceId) &&
        this.isLikeSearchedObject(this.selectedAcknowledgement, row.acknowledgementType) &&
        this.isTimeOnSelectedDate(row.creationTime);
      });
      return data;
    },
    severities() {
      const result = this.severityLevels.map(row => row.severityLevel);
      return Array.from(new Set(result)).sort();
    },
    sourceTypes() {
      const result = this.items.map(row => row.source);
      return Array.from(new Set(result)).sort();
    },
    eventTypes() {
        const result = this.items.map(row => {
            if (row.eventType == 'VEHICLE_ASSIGNMENT_FAILED' && row.eventData && row.eventData.indexOf('COURSE_NUMBER_MISSING') > -1)
                return 'COURSE_NUMBER_MISSING';

            return row.eventType;
        });
      return Array.from(new Set(result)).sort();
    },
    acknowledgments() {
      const result = this.items.map(row => row.acknowledgementType);
      return Array.from(new Set(result)).sort();
    },
    sortedTableData() {
      return this.filteredTableData.slice().sort((a, b) => b.creationTime.localeCompare(a.creationTime));
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = Math.min(start + this.itemsPerPage, this.sortedTableData.length);
      return this.sortedTableData.slice(start, end);
    }
  },
  mounted() {
    this.updateSeverityLevels();
    this.updateData();
  },
  beforeCreate() {
    if(!this.$store.state.systemEvents) {
        this.$store.registerModule("systemEvents", systemEvents);
    }
  },
  methods: {
    onUpdateAck(clickedEvent) {
      this.$store.dispatch('systemEvents/setSystemEventAck', clickedEvent);
    },
    onSeverityChanged(newSelection) {
      this.selectedSeverity = newSelection;
      this.setCurrentPageToFirst();
    },
    onEventTypeChanged(newSelection) {
      this.selectedEventType = newSelection;
      this.setCurrentPageToFirst();
    },
    onSourceTypeChanged(newSelection) {
      this.selectedSourceType = newSelection;
      this.setCurrentPageToFirst();
    },
    onAcknowledgmentChanged(newSelection) {
      this.selectedAcknowledgement = newSelection;
      this.setCurrentPageToFirst();
    },
    setCurrentPageToFirst() {
      this.currentPage = 1; 
    },
    onSourceSearchChanged() {
      this.setCurrentPageToFirst();
    },
    filterTableDataByDate() {
      this.setCurrentPageToFirst(); 
      this.updateSystemEventsList();
    },
    onPageChange(page) {
			this.currentPage = page;
		},
    onPerPageChange(perPage) {
			this.itemsPerPage = perPage;
		},
    getTranslationForTableRow(translationType, tableRow) {
      let resultString = 'system_event.objects_in_filters_and_table.';
      if(!translationType || !tableRow) {
        return undefined;
      }
      resultString += translationType +'.';
      resultString += tableRow;
      const translation = this.$t(resultString);
      if(translation == resultString) {
        return undefined;
      }
      return translation;
    },
    getSeverityForEventType(eventType, severityLevels) {
      for (const severityObj of severityLevels) {
        if (severityObj && severityObj.eventType == eventType && severityObj.severityLevel) {
          return severityObj.severityLevel;
        }
      }
      return undefined;
    },
    formatDate(date) {
      let time = new moment(date, this.dateFormat).format(
        'DD.MM.YYYY HH:mm'
      )
      if (time.startsWith('Invalid')) {
        return ''
      }
      return time
    },
    isTimeOnSelectedDate(eventTime) {
      let time = new moment(eventTime, this.dateFormat);
      return time.isSame(this.selectedDate, 'day');
    },
    isLikeSearchedObject(selectedObject, record) {
      return (record || record === "") && record.toLowerCase().includes(selectedObject.toLowerCase())
    },
    isLikeSearchedEventType(selectedEventType, rowEventType, rowEventData) {
        try {
            let eventData = JSON.parse(rowEventData);
            if (selectedEventType == 'COURSE_NUMBER_MISSING' &&
                selectedEventType == eventData.failReason)
                return true;
        } catch (e) {
            console.log(e);
        }
        return this.isLikeSearchedObject(selectedEventType, rowEventType);
    },
    updateData() {
      this.updateSystemEventsList();
			clearTimeout(this.dataUpdater);
			this.dataUpdater = setTimeout(() => {
				this.dataUpdateTime = Date.now();
			}, 30000);
		},
    updateSeverityLevels() {
      this.$store.dispatch('systemEvents/updateSeverityLevelsList');
    },
    updateSystemEventsList() {
      const dateParams = {
        startDate: this.selectedDate,
        endDate: this.selectedDate
      }
			this.$store.dispatch('systemEvents/updateSystemEventsList', dateParams);
    },
    showSendMessageToVehicle() {
    return this.$UiConfig('/ui/actions/showSendMessageToVehicle', false);
  },
  },
  watch: {
		dataUpdateTime: function () {
			this.updateData();
		},
	}
}
</script>

<style scoped>
.container {
  margin-top: 20px;
}
.acknowledgeColumn {
  text-align: center; 
  vertical-align: middle;
  align-items: center !important;
}
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.datepicker {
  transform: none !important;
  top: 100% !important;
  bottom: auto !important;
  z-index: 1050 !important;
}

.acknowledged-button {
  max-height: 45px;
  align-items: center !important;
  padding: 7px !important;
  cursor: not-allowed !important;
}
</style>
```

